/**
 * The till.
 *
 * Everything on this screen is built around one fact: the cashier's hands are on a scanner
 * and a keyboard, not a mouse, and a customer is watching. So the scan box keeps focus
 * whatever else happens, every action has a key, and the totals are large enough to read
 * from the customer's side of the counter.
 *
 * The cart lives here and nowhere else. Nothing is written until payment is taken, so an
 * abandoned sale leaves nothing behind on the server; and the prices shown are a quote --
 * the server re-prices the basket at checkout and its answer is what goes on the receipt.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Barcode,
  Percent,
  Search,
  Trash2,
  UserPlus,
  UserX,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useRegime } from '@/features/company/regime'
import { searchCustomers, type CustomerOption } from '@/features/orders/api'
import {
  checkout,
  formatMoney,
  getStoreCredit,
  listTerminals,
  newIdempotencyKey,
  rememberTerminal,
  rememberedTerminal,
  scanCode,
  searchCatalog,
  type TillProduct,
} from '@/features/pos/api'
import { PaymentDialog, type PaymentResult } from '@/features/pos/PaymentDialog'
import {
  addToCart,
  discountFromPercent,
  fromCents,
  lineGrossCents,
  parseScan,
  priceCart,
  removeLine,
  setLineDiscount,
  setQuantity,
  toCents,
  type CartLine,
} from '@/features/pos/cart'
import { toast } from '@/lib/toast'

/** Printed on the screen so nobody has to be told twice. */
const SHORTCUTS: [string, string][] = [
  ['Enter', 'Add the scanned code'],
  ['12*CODE', 'Twelve of it'],
  ['F2', 'Payment'],
  ['F4', 'Customer'],
  ['F8', 'Discount the sale'],
  ['Ctrl+K', 'Search by name, brand or SKU'],
  ['↑ ↓', 'Pick a line'],
  ['+ −', 'Change the quantity'],
  ['Delete', 'Remove the line'],
]

export function PointOfSale() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const regime = useRegime()

  const [terminalId, setTerminalId] = useState<string | null>(rememberedTerminal)
  const [lines, setLines] = useState<CartLine[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [customer, setCustomer] = useState<CustomerOption | null>(null)
  const [saleDiscount, setSaleDiscount] = useState('')
  const [scan, setScan] = useState('')
  const [dialog, setDialog] = useState<'none' | 'payment' | 'customer' | 'search' | 'discount'>(
    'none',
  )

  // What the search opens with, and how many of the item it adds: a name typed into the
  // scan box ("3*amul butter") carries over rather than being typed twice.
  const [searchSeed, setSearchSeed] = useState({ q: '', quantity: 1 })
  const scanBox = useRef<HTMLInputElement>(null)
  const focusScan = useCallback(() => scanBox.current?.focus(), [])

  const terminals = useQuery({ queryKey: ['pos', 'terminals'], queryFn: () => listTerminals() })
  const terminal = useMemo(
    () => terminals.data?.find((row) => row.id === terminalId) ?? null,
    [terminals.data, terminalId],
  )

  // One till per counter, so a single active terminal needs no picker at all.
  useEffect(() => {
    if (terminalId || !terminals.data?.length) return
    const only = terminals.data.filter((row) => row.status === 'active')
    if (only.length === 1) setTerminalId(only[0].id)
  }, [terminals.data, terminalId])

  useEffect(() => rememberTerminal(terminalId), [terminalId])

  const credit = useQuery({
    queryKey: ['pos', 'store-credit', customer?.id],
    queryFn: () => getStoreCredit(customer!.id),
    enabled: Boolean(customer),
    staleTime: 0,
  })

  const totals = useMemo(
    () => priceCart(lines, toCents(saleDiscount), regime.rounds_to_whole_currency),
    [lines, saleDiscount, regime.rounds_to_whole_currency],
  )

  // --- adding to the cart ---------------------------------------------------------------

  const add = useCallback(
    (product: TillProduct, quantity = 1) => {
      setLines((current) => addToCart(current, product, quantity))
      setSelected(product.id)
      setScan('')
      focusScan()
    },
    [focusScan],
  )

  const lookup = useMutation({
    mutationFn: ({ code, quantity }: { code: string; quantity: number }) =>
      scanCode(code, terminal?.location_id).then((product) => ({ product, quantity })),
    onSuccess: ({ product, quantity }) => add(product, quantity),
    onError: (error) => {
      // A code the catalog does not know is the one error a cashier meets hourly. Say what
      // was not found, keep it in the box so it can be corrected, and offer the search.
      const message =
        error instanceof ApiError && error.status === 404
          ? `No item with code “${scan.trim()}”. Press Ctrl+K to search by name.`
          : error instanceof ApiError
            ? error.message
            : 'Could not reach the till service.'
      toast.error(message)
      scanBox.current?.select()
    },
  })

  const submitScan = useCallback(() => {
    const parsed = parseScan(scan)
    if (!parsed || lookup.isPending) return
    // Codes have no spaces; names do. A name typed here is a search, not a failed scan.
    if (/\s/.test(parsed.code)) {
      setSearchSeed({ q: parsed.code, quantity: parsed.quantity })
      setScan('')
      setDialog('search')
      return
    }
    lookup.mutate(parsed)
  }, [scan, lookup])

  const openSearch = useCallback(() => {
    setSearchSeed({ q: '', quantity: 1 })
    setDialog('search')
  }, [])

  // --- checkout -------------------------------------------------------------------------

  /**
   * Minted once per submission and reused across retries of that submission.
   *
   * This is the whole duplicate-sale defence on the client side. A cashier whose network
   * drops mid-checkout presses the button again, and the same key means the server replays
   * the first sale instead of ringing up a second. It is cleared only when we know the
   * outcome: on success, and on a rejection the server made before writing anything.
   */
  const idempotencyKey = useRef<string | null>(null)

  const sale = useMutation({
    mutationFn: (payment: PaymentResult) => {
      idempotencyKey.current ??= newIdempotencyKey()
      return checkout(
        {
          terminal_id: terminal!.id,
          items: totals.lines.map((line) => ({
            product_id: line.product.id,
            quantity: line.quantity,
            discount_amount: fromCents(line.discountCents),
          })),
          payments: payment.payments,
          customer_id: customer?.id ?? null,
          discount_amount: fromCents(toCents(saleDiscount)),
          cash_tendered: payment.cash_tendered,
        },
        idempotencyKey.current,
      )
    },
    onSuccess: (created) => {
      idempotencyKey.current = null
      void queryClient.invalidateQueries({ queryKey: ['pos'] })
      setLines([])
      setCustomer(null)
      setSaleDiscount('')
      setSelected(null)
      setDialog('none')
      navigate(`/pos/sales/${created.id}`)
    },
    onError: (error) => {
      // A 4xx is the server saying it did not write the sale -- a fresh key next time is
      // right, and reusing this one would replay a rejection. Anything else (a timeout, a
      // 500) leaves the outcome unknown, so the key is kept and the retry is a replay.
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        idempotencyKey.current = null
      }
    },
  })

  const checkoutError =
    sale.error instanceof ApiError
      ? sale.error.message
      : sale.error
        ? 'The sale did not go through. Check the network and try again.'
        : null

  // --- keyboard -------------------------------------------------------------------------

  const openShiftId = terminal?.open_shift_id ?? null
  const canSell = Boolean(terminal && openShiftId && lines.length > 0)

  /**
   * One listener, not several.
   *
   * Two `keydown` handlers on `window` would race: React re-registers each effect on its
   * own dependencies, so which of them saw a key first would change as the sale went on --
   * and "+" would sometimes raise a quantity and sometimes be typed into the scan box.
   */
  useEffect(() => {
    if (dialog !== 'none') return

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement

      if (event.key === 'F2') {
        event.preventDefault()
        if (canSell) setDialog('payment')
        return
      }
      if (event.key === 'F4') {
        event.preventDefault()
        setDialog('customer')
        return
      }
      if (event.key === 'F8') {
        event.preventDefault()
        setDialog('discount')
        return
      }
      if (event.key.toLowerCase() === 'k' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        openSearch()
        return
      }

      // Any other field on the screen -- a quantity, a line discount -- keeps its keys.
      if (typing && target !== scanBox.current) return

      // The line keys work while the scan box has focus, as long as nothing is half typed
      // in it: a cashier changing a quantity has not put the scanner down, and making them
      // click the row first is exactly the mouse trip this screen exists to avoid.
      const scanBusy = target === scanBox.current && scan.length > 0
      const line = selected ? lines.find((row) => row.product.id === selected) : undefined

      if (!scanBusy && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        if (lines.length === 0) return
        event.preventDefault()
        const at = lines.findIndex((row) => row.product.id === selected)
        const step = event.key === 'ArrowDown' ? 1 : -1
        const next = (at === -1 ? 0 : at + step + lines.length) % lines.length
        setSelected(lines[next].product.id)
        return
      }

      if (!scanBusy && line && selected) {
        if (event.key === '+' || event.key === '=') {
          event.preventDefault()
          setLines((current) => setQuantity(current, selected, line.quantity + 1))
          return
        }
        if (event.key === '-' || event.key === '_') {
          event.preventDefault()
          setLines((current) => setQuantity(current, selected, line.quantity - 1))
          return
        }
        if (event.key === 'Delete') {
          event.preventDefault()
          setLines((current) => removeLine(current, selected))
          setSelected(null)
          return
        }
      }

      // A scanner is a keyboard that types very fast and then presses Enter. If focus has
      // wandered -- a click on a row, a dismissed toast -- the opening characters of the
      // barcode would be lost, so anything printable typed outside a field is put back
      // into the scan box.
      if (!typing && !event.ctrlKey && !event.altKey && !event.metaKey && event.key.length === 1) {
        event.preventDefault()
        setScan((current) => current + event.key)
        focusScan()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [dialog, lines, selected, scan, canSell, focusScan, openSearch])

  useEffect(() => {
    if (dialog === 'none') focusScan()
  }, [dialog, focusScan])

  // --- what the screen shows ------------------------------------------------------------

  if (terminals.isLoading) {
    return <p className="p-6 text-sm text-slate-500">Finding the till…</p>
  }

  if (terminals.error) {
    return (
      <Alert tone="error" title="Could not reach the till service">
        {terminals.error instanceof ApiError
          ? terminals.error.message
          : 'Check the network and try again.'}
      </Alert>
    )
  }

  if (!terminal) {
    return (
      <TerminalPicker
        terminals={terminals.data ?? []}
        onPick={(id) => {
          setTerminalId(id)
          focusScan()
        }}
      />
    )
  }

  if (!openShiftId) {
    return (
      <div className="mx-auto max-w-lg space-y-4 p-6 text-center">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {terminal.name} is closed
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Nothing can be sold on this till until a shift is open with the cash that is in the
          drawer. That count is what the day is reconciled against.
        </p>
        <Button onClick={() => navigate('/pos/shift')}>Open a shift</Button>
      </div>
    )
  }

  const storeCreditCents = customer
    ? credit.data
      ? toCents(credit.data.balance)
      : 0
    : null

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {terminal.name}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {terminal.code} · shift open
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setDialog('customer')}>
            {customer ? <UserX className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {customer ? customer.name : 'Customer (F4)'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => navigate('/pos/shift')}>
            Shift
          </Button>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              submitScan()
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Barcode
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <Input
                ref={scanBox}
                aria-label="Scan or type a product code"
                placeholder="Scan a barcode, type a code, or type a name to search"
                className="h-12 pl-11 text-base"
                autoComplete="off"
                value={scan}
                onChange={(event) => setScan(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setScan('')
                }}
              />
            </div>
            <Button type="submit" size="md" className="h-12" loading={lookup.isPending}>
              Add
            </Button>
            <Button
              variant="secondary"
              className="h-12"
              onClick={openSearch}
              aria-label="Search products"
              title="Search by name, brand, SKU or category (Ctrl+K)"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Search</span>
              <kbd className="hidden rounded border border-slate-300 px-1 text-[10px] text-slate-500 lg:inline dark:border-slate-600">
                Ctrl+K
              </kbd>
            </Button>
          </form>

          <CartTable
            totals={totals}
            selected={selected}
            onSelect={setSelected}
            onQuantity={(id, quantity) => setLines((current) => setQuantity(current, id, quantity))}
            onDiscount={(id, cents) => setLines((current) => setLineDiscount(current, id, cents))}
            onRemove={(id) => {
              setLines((current) => removeLine(current, id))
              setSelected(null)
              focusScan()
            }}
          />
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <dl className="space-y-2 text-sm">
              <Row label="Items" value={String(totals.itemCount)} />
              {/* Before discounts, so the column reads down: subtotal, less discount, plus
                  tax, is the total. `subtotalCents` is already net of the discount. */}
              <Row
                label="Subtotal"
                value={formatMoney(fromCents(totals.subtotalCents + totals.discountCents))}
              />
              {totals.discountCents > 0 && (
                <Row
                  label="Discount"
                  value={`− ${formatMoney(fromCents(totals.discountCents))}`}
                />
              )}
              {totals.taxBands.map((band) => (
                <Row
                  key={band.rate}
                  label={`Tax at ${band.rate}%`}
                  value={formatMoney(fromCents(band.taxCents))}
                />
              ))}
              {totals.roundOffCents !== 0 && (
                <Row label="Rounding" value={formatMoney(fromCents(totals.roundOffCents))} />
              )}
            </dl>

            <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Total
              </p>
              <p className="text-4xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {formatMoney(fromCents(totals.totalCents))}
              </p>
            </div>

            <div className="mt-4 space-y-2">
              <Button className="h-12 w-full text-base" disabled={!canSell} onClick={() => setDialog('payment')}>
                Payment (F2)
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                  onClick={() => setDialog('discount')}
                >
                  <Percent className="h-4 w-4" />
                  Discount (F8)
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  disabled={lines.length === 0}
                  onClick={() => {
                    setLines([])
                    setSelected(null)
                    setSaleDiscount('')
                    focusScan()
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>

          {customer && (
            <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm dark:border-slate-800 dark:bg-slate-900">
              <p className="font-medium text-slate-900 dark:text-slate-100">{customer.name}</p>
              {customer.tax_id && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {customer.tax_id}
                  {customer.state ? ` · ${customer.state}` : ''}
                </p>
              )}
              <p className="text-slate-500 dark:text-slate-400">
                {credit.isLoading
                  ? 'Checking store credit…'
                  : `${formatMoney(credit.data?.balance ?? '0')} store credit`}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => {
                  setCustomer(null)
                  focusScan()
                }}
              >
                <X className="h-4 w-4" />
                Walk-in sale
              </Button>
            </div>
          )}

          <dl className="rounded-lg border border-dashed border-slate-300 p-4 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
            {SHORTCUTS.map(([key, meaning]) => (
              <div key={key} className="flex justify-between gap-4 py-0.5">
                <dt className="font-mono font-medium text-slate-600 dark:text-slate-300">{key}</dt>
                <dd>{meaning}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <PaymentDialog
        open={dialog === 'payment'}
        onClose={() => {
          if (!sale.isPending) setDialog('none')
        }}
        totalCents={totals.totalCents}
        storeCreditCents={storeCreditCents}
        pending={sale.isPending}
        error={checkoutError}
        onConfirm={(result) => sale.mutate(result)}
      />

      <CustomerDialog
        open={dialog === 'customer'}
        onClose={() => setDialog('none')}
        onPick={(picked) => {
          setCustomer(picked)
          setDialog('none')
        }}
      />

      <CatalogSearchDialog
        open={dialog === 'search'}
        initialQuery={searchSeed.q}
        quantity={searchSeed.quantity}
        locationId={terminal?.location_id}
        onClose={() => setDialog('none')}
        onPick={(product) => {
          add(product, searchSeed.quantity)
          setDialog('none')
          const count = searchSeed.quantity > 1 ? `${searchSeed.quantity} × ` : ''
          toast.success(`Added ${count}${product.name} (SKU ${product.code})`)
        }}
      />

      <DiscountDialog
        open={dialog === 'discount'}
        onClose={() => setDialog('none')}
        sellableCents={totals.subtotalCents + totals.discountCents}
        value={saleDiscount}
        onApply={(amount) => {
          setSaleDiscount(amount)
          setDialog('none')
        }}
      />
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="tabular-nums text-slate-900 dark:text-slate-100">{value}</dd>
    </div>
  )
}

function TerminalPicker({
  terminals,
  onPick,
}: {
  terminals: { id: string; code: string; name: string; status: string; open_shift_id: string | null }[]
  onPick: (id: string) => void
}) {
  if (terminals.length === 0) {
    return (
      <Alert tone="info" title="No till at this location">
        A terminal has to be registered against the store before anything can be sold from
        it. An administrator does that once, in settings.
      </Alert>
    )
  }
  return (
    <div className="mx-auto max-w-lg space-y-3 p-6">
      <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
        Which till is this?
      </h1>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Asked once. The browser remembers it, because a till is a machine on a counter
        rather than a preference of whoever is signed in.
      </p>
      {terminals.map((terminal) => (
        <button
          key={terminal.id}
          type="button"
          onClick={() => onPick(terminal.id)}
          disabled={terminal.status !== 'active'}
          className="flex w-full items-center justify-between rounded-md border border-slate-200 p-4 text-left hover:bg-slate-50 disabled:opacity-50 dark:border-slate-800 dark:hover:bg-slate-800"
        >
          <span>
            <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
              {terminal.name}
            </span>
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              {terminal.code}
            </span>
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {terminal.status !== 'active'
              ? terminal.status
              : terminal.open_shift_id
                ? 'shift open'
                : 'closed'}
          </span>
        </button>
      ))}
    </div>
  )
}

function CartTable({
  totals,
  selected,
  onSelect,
  onQuantity,
  onDiscount,
  onRemove,
}: {
  totals: ReturnType<typeof priceCart>
  selected: string | null
  onSelect: (id: string) => void
  onQuantity: (id: string, quantity: number) => void
  onDiscount: (id: string, cents: number) => void
  onRemove: (id: string) => void
}) {
  if (totals.lines.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-12 text-center dark:border-slate-700">
        <Barcode className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" aria-hidden="true" />
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          Scan the first item to start a sale.
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
      <table className="w-full text-sm">
        <caption className="sr-only">Items in this sale</caption>
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
          <tr>
            <th scope="col" className="px-4 py-2">Item</th>
            <th scope="col" className="px-4 py-2 text-right">Price</th>
            <th scope="col" className="px-4 py-2 text-center">Qty</th>
            <th scope="col" className="px-4 py-2 text-right">Discount</th>
            <th scope="col" className="px-4 py-2 text-right">Total</th>
            <th scope="col" className="px-2 py-2"><span className="sr-only">Remove</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
          {totals.lines.map((line) => (
            <tr
              key={line.product.id}
              onClick={() => onSelect(line.product.id)}
              aria-selected={line.product.id === selected}
              className={
                line.product.id === selected
                  ? 'bg-brand-50 dark:bg-brand-900/30'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
              }
            >
              <td className="px-4 py-2">
                <span className="block font-medium text-slate-900 dark:text-slate-100">
                  {line.product.name}
                </span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  {line.product.code}
                  {Number(line.product.promo_pct) > 0 && (
                    <span className="ml-2 text-success">{Number(line.product.promo_pct)}% off</span>
                  )}
                  {line.product.quantity != null && line.quantity > line.product.quantity && (
                    <span className="ml-2 text-warning">
                      only {line.product.quantity} on hand
                    </span>
                  )}
                </span>
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {formatMoney(line.product.price)}
              </td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  min={1}
                  max={10000}
                  aria-label={`Quantity of ${line.product.name}`}
                  value={line.quantity}
                  onChange={(event) => onQuantity(line.product.id, Number(event.target.value))}
                  className="mx-auto block h-8 w-16 rounded border border-slate-300 bg-white text-center text-sm tabular-nums dark:border-slate-700 dark:bg-slate-900"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  inputMode="decimal"
                  aria-label={`Discount on ${line.product.name}`}
                  placeholder="0.00"
                  value={line.discountCents ? fromCents(line.discountCents) : ''}
                  onChange={(event) =>
                    onDiscount(line.product.id, toCents(event.target.value))
                  }
                  className="ml-auto block h-8 w-24 rounded border border-slate-300 bg-white px-2 text-right text-sm tabular-nums dark:border-slate-700 dark:bg-slate-900"
                />
                <span className="sr-only">
                  of {formatMoney(fromCents(lineGrossCents(line)))}
                </span>
              </td>
              <td className="px-4 py-2 text-right font-medium tabular-nums">
                {formatMoney(fromCents(line.totalCents))}
              </td>
              <td className="px-2 py-2">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${line.product.name}`}
                  onClick={() => onRemove(line.product.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CustomerDialog({
  open,
  onClose,
  onPick,
}: {
  open: boolean
  onClose: () => void
  onPick: (customer: CustomerOption) => void
}) {
  const [q, setQ] = useState('')
  const results = useQuery({
    queryKey: ['pos', 'customers', q],
    queryFn: () => searchCustomers(q),
    enabled: open,
  })

  return (
    <Modal open={open} onClose={onClose} title="Attach a customer" description="For store credit, to email the receipt, and to bill a registered buyer by name and GSTIN.">
      <div className="space-y-3">
        <Input
          aria-label="Search customers"
          placeholder="Name, code or phone"
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
        <ul className="max-h-72 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-800">
          {(results.data?.data ?? []).map((customer) => (
            <li key={customer.id}>
              <button
                type="button"
                onClick={() => onPick(customer)}
                className="w-full px-1 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <span className="block font-medium text-slate-900 dark:text-slate-100">
                  {customer.name}
                </span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">
                  {/* The registration, where there is one: it is what makes the sale a B2B
                      document, and the cashier is the only person who can catch the wrong
                      account being attached before the roll is torn off. */}
                  {[customer.code, customer.tax_id, customer.state].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
          {results.isFetched && (results.data?.data.length ?? 0) === 0 && (
            <li className="py-3 text-sm text-slate-500 dark:text-slate-400">
              Nobody matches that. The sale can go through as a walk-in.
            </li>
          )}
        </ul>
      </div>
    </Modal>
  )
}

function CatalogSearchDialog({
  open,
  initialQuery = '',
  quantity = 1,
  ...rest
}: {
  open: boolean
  onClose: () => void
  locationId?: string | null
  onPick: (product: TillProduct) => void
  /** Text typed into the scan box that turned out to be a name. */
  initialQuery?: string
  /** How many a pick adds, when the scan box said "3*name". */
  quantity?: number
}) {
  if (!open) return null
  // Mounted per opening, so each search starts from its own seed and a fresh highlight.
  return <CatalogSearch initialQuery={initialQuery} quantity={quantity} {...rest} />
}

function CatalogSearch({
  onClose,
  onPick,
  locationId,
  initialQuery,
  quantity,
}: {
  onClose: () => void
  locationId?: string | null
  onPick: (product: TillProduct) => void
  initialQuery: string
  quantity: number
}) {
  const [q, setQ] = useState(initialQuery)
  const [term, setTerm] = useState(initialQuery.trim())
  const [active, setActive] = useState(0)
  const box = useRef<HTMLInputElement>(null)

  // The modal focuses its first control, the close button, after this runs; the cashier is
  // about to type, so the box takes focus back on the next tick.
  useEffect(() => {
    const timer = setTimeout(() => {
      box.current?.focus()
      box.current?.select()
    })
    return () => clearTimeout(timer)
  }, [])

  // One query per pause in typing, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(q.trim()), 200)
    return () => clearTimeout(timer)
  }, [q])

  const results = useQuery({
    queryKey: ['pos', 'catalog', term, locationId],
    queryFn: () => searchCatalog(term, 20, locationId),
    enabled: term.length > 0,
  })
  const found = results.data ?? []
  const highlighted = Math.min(active, Math.max(0, found.length - 1))

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive(Math.min(highlighted + 1, found.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive(Math.max(highlighted - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      // Only a result for what is typed now: Enter mid-debounce would add a match for the
      // previous words.
      const pick = found[highlighted]
      if (pick && term === q.trim()) onPick(pick)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Search products"
      description={`By name, brand, SKU or category. Check the SKU, then press Enter or click to add${quantity > 1 ? ` ${quantity}` : ''}.`}
    >
      <div className="space-y-3">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <Input
            ref={box}
            aria-label="Search the catalog"
            placeholder="e.g. amul butter, SKU-00030, dairy"
            className="pl-9"
            autoComplete="off"
            value={q}
            onChange={(event) => {
              setQ(event.target.value)
              setActive(0)
            }}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded={found.length > 0}
            aria-controls="pos-search-results"
            aria-activedescendant={found.length > 0 ? `pos-search-${highlighted}` : undefined}
          />
        </div>
        <ul
          id="pos-search-results"
          role="listbox"
          aria-label="Matching products"
          className="max-h-80 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-800"
        >
          {found.map((product, index) => {
            const promo = Number(product.promo_pct || 0)
            const net = ((Number(product.price) * (100 - promo)) / 100).toFixed(2)
            const outOfStock = product.quantity != null && product.quantity <= 0
            return (
              <li
                key={product.id}
                id={`pos-search-${index}`}
                role="option"
                aria-selected={index === highlighted}
                onMouseEnter={() => setActive(index)}
                onClick={() => onPick(product)}
                className={
                  'flex cursor-pointer items-center justify-between gap-4 rounded px-2 py-2 text-sm ' +
                  (index === highlighted
                    ? 'bg-brand-50 dark:bg-brand-900/30'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800')
                }
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-900 dark:text-slate-100">
                    {product.name}
                  </span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      SKU {product.code}
                    </span>
                    {product.category_name && <span>{product.category_name}</span>}
                    <span>{product.unit_of_measure}</span>
                    {product.quantity != null && (
                      <span
                        className={
                          outOfStock ? 'font-medium text-amber-700 dark:text-amber-400' : undefined
                        }
                      >
                        {outOfStock ? 'None on hand here' : `${product.quantity} on hand`}
                      </span>
                    )}
                  </span>
                </span>
                <span className="shrink-0 text-right tabular-nums">
                  {promo > 0 ? (
                    <>
                      <span className="block font-medium">{formatMoney(net)}</span>
                      <span className="block text-xs text-slate-400 line-through">
                        {formatMoney(product.price)}
                      </span>
                    </>
                  ) : (
                    formatMoney(product.price)
                  )}
                </span>
              </li>
            )
          })}
        </ul>
        {term.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Type a few letters of the name, the brand or the SKU.
          </p>
        )}
        {term.length > 0 && results.isFetched && found.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Nothing matches “{term}”.</p>
        )}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          ↑ ↓ to choose · Enter to add · Esc to go back to scanning
        </p>
      </div>
    </Modal>
  )
}

/**
 * Money off the whole sale, typed either way round.
 *
 * A percentage is what a cashier is told ("give them ten percent") and an amount is what
 * the receipt has to add up to, so the percentage is converted here, once, and the sale
 * carries the amount from then on.
 */
function DiscountDialog({
  open,
  onClose,
  sellableCents,
  value,
  onApply,
}: {
  open: boolean
  onClose: () => void
  sellableCents: number
  value: string
  onApply: (amount: string) => void
}) {
  const [amount, setAmount] = useState(value)
  const [percent, setPercent] = useState('')

  useEffect(() => {
    if (open) {
      setAmount(value)
      setPercent('')
    }
  }, [open, value])

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Discount the sale"
      description={`Spread across the lines in proportion. ${formatMoney(fromCents(sellableCents))} before discount.`}
      footer={
        <>
          <Button variant="secondary" onClick={() => onApply('')}>
            Remove
          </Button>
          <Button onClick={() => onApply(amount)}>Apply</Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1.5 text-sm">
          <span className="block font-medium text-slate-700 dark:text-slate-300">Amount</span>
          <Input
            inputMode="decimal"
            placeholder="0.00"
            className="text-right tabular-nums"
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value)
              setPercent('')
            }}
          />
        </label>
        <label className="space-y-1.5 text-sm">
          <span className="block font-medium text-slate-700 dark:text-slate-300">Percent</span>
          <Input
            inputMode="decimal"
            placeholder="0"
            className="text-right tabular-nums"
            value={percent}
            onChange={(event) => {
              setPercent(event.target.value)
              setAmount(fromCents(discountFromPercent(sellableCents, Number(event.target.value))))
            }}
          />
        </label>
      </div>
    </Modal>
  )
}
